"use client"

import { useState, useEffect } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faChevronDown, faSpinner } from "@fortawesome/free-solid-svg-icons"
import { Dropdown, DropdownItem } from "../../ui/dropdown"
import styles from "./StatusSelector.module.css"

const StatusSelector = ({
  id,
  currentStatus,
  onStatusChange,
  statusColorMap,
  disabled = false,
  openDropdownId = undefined,
  setOpenDropdownId = undefined,
}) => {
  const [internalOpen, setInternalOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const options = statusColorMap
    ? Object.entries(statusColorMap).map(([key, colors]) => ({
        value: key,
        label: key,
        color: colors.color,
        text: colors.text,
        dotColor: colors.dotColor,
      }))
    : []

  const current =
    options.find(
      (opt) =>
        opt.value === currentStatus ||
        opt.label === currentStatus ||
        opt.value.toLowerCase() === currentStatus?.toLowerCase() ||
        opt.label.toLowerCase() === currentStatus?.toLowerCase(),
    ) || options[0] || {}

  const isControlled = typeof openDropdownId !== "undefined" && typeof setOpenDropdownId === "function"
  const isOpen = isControlled ? openDropdownId === id : internalOpen

  const toggleOpen = (nextId) => {
    if (isControlled) {

      setOpenDropdownId(nextId)
    } else {
      setInternalOpen((prev) => (nextId === id ? !prev : Boolean(nextId === id)))
    }
  }

  const close = () => {
    if (isControlled) setOpenDropdownId(null)
    else setInternalOpen(false)
  }

  const handleStatusChange = async (newStatus) => {
    if (!onStatusChange) return
    setIsLoading(true)
    try {
      await onStatusChange(newStatus)
    } catch (err) {
      console.error("Status update failed", err)
    } finally {
      setIsLoading(false)
      close()
    }
  }

  useEffect(() => {
    const handler = () => {
      close()
    }
    document.addEventListener("closeAllDropdowns", handler)
    return () => document.removeEventListener("closeAllDropdowns", handler)

  }, [openDropdownId, setOpenDropdownId])

  const BadgeTrigger = (
    <div
      className={`${styles.status_badge} ${disabled || isLoading ? styles.disabled : ""}`}
      style={{
        backgroundColor: current.color,
        color: current.text,
        cursor: disabled || isLoading ? "not-allowed" : "pointer",
        userSelect: "none",
      }}
      onClick={(e) => {
        e.stopPropagation()
        if (disabled || isLoading) return

        toggleOpen(isOpen ? null : id)
      }}
    >
      <div className={styles.badge_content}>
        <div className={styles.status_dot} style={{ backgroundColor: current.dotColor }} />
        <span className={styles.badge_text}>{current.label}</span>
      </div>

      {!disabled && !isLoading && <FontAwesomeIcon icon={faChevronDown} className={styles.dropdown_icon} />}
      {isLoading && <FontAwesomeIcon icon={faSpinner} spin className={styles.spinner_icon} />}
    </div>
  )

  return (
    <Dropdown
      options={options}
      value={currentStatus}
      onChange={handleStatusChange}
      trigger={BadgeTrigger}
      align="left"
      className={styles.dropdown_menu}
      disabled={disabled || isLoading}
      isOpen={isOpen}
      onClose={() => {

        close()
      }}
    >
      {options
        .filter((opt) => opt.value !== currentStatus)
        .map((opt) => (
          <DropdownItem
            key={opt.value}
            value={opt.value}
            className={styles.dropdown_item}
            onClick={(e) => {
              e.stopPropagation()
              handleStatusChange(opt.value)
            }}
          >
            <div className={styles.dropdown_item_content}>
              <div className={styles.dropdown_dot} style={{ backgroundColor: opt.dotColor }} />
              <span className={styles.dropdown_text}>{opt.label}</span>
            </div>
          </DropdownItem>
        ))}
    </Dropdown>
  )
}

export default StatusSelector